import React, { useEffect } from 'react';
import { BusinessClaimsVendorEditor } from '../../components/reporting/BusinessClaimsVendorEditor';
import { useScanBusinessClaimsVendors } from '../../hooks/useBusinessClaimsPrep';
import { vendorRowStatesFromGroups } from '../../services/business-claims-wizard-utils';
import type { BusinessClaimsVendorRowState } from '../../types/business-claims.types';

export interface BusinessClaimsStepState {
  rows: BusinessClaimsVendorRowState[];
  overwriteExisting: boolean;
  loadedForKey: string | null;
  loadError: string | null;
}

export const emptyBusinessClaimsStepState = (): BusinessClaimsStepState => ({
  rows: [],
  overwriteExisting: false,
  loadedForKey: null,
  loadError: null,
});

interface Props {
  dateFrom: string;
  dateTo: string;
  dateRangeLabel: string;
  state: BusinessClaimsStepState;
  onChange: (state: BusinessClaimsStepState) => void;
}

export function ReportWizardStepBusinessClaims({
  dateFrom,
  dateTo,
  dateRangeLabel,
  state,
  onChange,
}: Props): React.ReactElement {
  const scanMutation = useScanBusinessClaimsVendors();
  const loadKey = `${dateFrom}|${dateTo}`;

  useEffect(() => {
    if (state.loadedForKey === loadKey) return;

    let cancelled = false;
    void (async () => {
      try {
        const groups = await scanMutation.mutateAsync({
          dateFrom,
          dateTo,
        });
        if (cancelled) return;
        onChange({
          rows: vendorRowStatesFromGroups(groups),
          overwriteExisting: state.overwriteExisting,
          loadedForKey: loadKey,
          loadError: null,
        });
      } catch (err) {
        if (!cancelled) {
          onChange({
            rows: [],
            overwriteExisting: state.overwriteExisting,
            loadedForKey: loadKey,
            loadError: err instanceof Error ? err.message : 'Failed to load vendors',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when period changes only
  }, [loadKey]);

  return (
    <BusinessClaimsVendorEditor
      rows={state.rows}
      onRowsChange={(rows) => onChange({ ...state, rows })}
      overwriteExisting={state.overwriteExisting}
      onOverwriteExistingChange={(overwriteExisting) =>
        onChange({ ...state, overwriteExisting })
      }
      loading={state.loadedForKey !== loadKey || scanMutation.isPending}
      error={state.loadedForKey === loadKey ? state.loadError : null}
      dateRangeLabel={dateRangeLabel}
    />
  );
}
